import * as twgl from "twgl.js";
import { rasterAssetDataUrl } from "@/document/assets/rasterAssets";
import { oklchToRgba } from "@/document/model/color";
import type { SceneNode } from "@/document/model/nodes";
import type {
  BackgroundBlurEffect,
  DropShadowEffect,
  InnerShadowEffect,
  LayerBlurEffect,
} from "@/document/model/styles";
import { boundsFromAffineRect } from "@/lib/math/affine";
import { rectCenter } from "@/lib/math/rotation";
import type { Camera } from "../camera";
import { GlBlurPass, MAX_GAUSSIAN_BLUR_SIGMA, documentBlurToSigma } from "../gl/blur";
import { createTexturedQuadBuffer, createTriangleBuffer } from "../gl/buffers";
import type { RenderPreview } from "../overlayTypes";
import {
  applyImageAdjustmentOverlays,
  canvasFillForDisplayPaint,
  canvasFilterForImagePaint,
  displayImageDrawRect,
  displayItemForCanvas,
  displayItemToTextNode,
  documentBoundsToPixelRect,
  drawShadowShapeMask,
  drawTextWithLetterSpacing,
  flipPixelRows,
  formatCacheNumber,
  neutralTextureUniforms,
  offsetSpreadBounds,
  rectVertices,
  roundedRectPath,
  shadowPoint,
  translateDisplayItem,
  verticalTextOffset,
} from "../renderHelpers";
import { getCachedTextLayout, paintToCanvasFillStyle } from "../text/canvasKitText";
import type { DisplayClip, DisplayItem, DisplayPaint } from "./displayList";
import {
  applyCanvasAffineTransform,
  debugTriangleColorForIndex,
  displayItemTriangleVertices,
} from "./displayTransform";
import {
  applyCanvasClip,
  clipUniforms,
  dropShadowBounds,
  dropShadowCacheKey,
  effectRasterScale,
  expandedBounds,
  fitClipPolygonToUniformLimit,
  intersectBounds,
  layerBlurRadius,
  maxShaderClipPointCount,
  shouldRasterizeClip,
  transformRectVertices,
} from "./displayItemGeometry";

const DROP_SHADOW_CACHE_LIMIT = 64;

/**
 * Cap device-space blur σ at the faithful Gaussian kernel size. Beyond this we
 * lower raster scale (and/or GlBlurPass downsamples) so document-space blur
 * stays constant across zoom instead of getting sharper when zoomed in.
 */
const MAX_EFFECT_BLUR_DEVICE_PX = MAX_GAUSSIAN_BLUR_SIGMA;

interface CachedImage {
  image: HTMLImageElement;
  loaded: boolean;
  failed: boolean;
  texture?: WebGLTexture;
  /** Shared in-flight load promise so concurrent preloads of the same key dedupe. */
  loadPromise?: Promise<void>;
}

interface CachedDropShadow {
  texture: WebGLTexture;
  width: number;
  height: number;
}

/**
 * Dependencies the {@link DisplayItemDrawer} needs from the owning {@link Renderer}.
 * These stay on the renderer because they are shared with tile/backdrop/debug paths.
 */
export interface DisplayItemHost {
  readonly gl: WebGL2RenderingContext;
  readonly programInfo: twgl.ProgramInfo;
  readonly ellipseProgramInfo: twgl.ProgramInfo;
  readonly roundedRectProgramInfo: twgl.ProgramInfo;
  readonly textureProgramInfo: twgl.ProgramInfo;
  readonly maxTextureSize: number;
  readonly renderTrianglesDebug: boolean;
  activeCamera(): Camera;
  activeResolution(): [number, number];
  activeViewportDocumentBounds(): RenderPreview["bounds"];
  baseUniforms(positionOffset?: { x: number; y: number }): Record<string, unknown>;
  paintUniforms(paint: DisplayPaint, positionOffset?: { x: number; y: number }, clip?: DisplayClip): Record<string, unknown>;
  displayTriangleBuffer(vertices: Float32Array | number[]): twgl.BufferInfo;
  shouldRenderBackgroundBlur(): boolean;
  /** Re-render the last frame after an async image finished loading. */
  reloadAfterImageLoad(): void;
}

/**
 * Draws display-list items, their effects (drop shadow, blurs, inner shadow),
 * rasterized fallbacks, image fills, and text. Owns the drop-shadow, image, and
 * text-texture caches that back those paths.
 */
export class DisplayItemDrawer {
  private readonly textTextureCache = new Map<string, { texture: WebGLTexture; width: number; height: number }>();
  private readonly imageCache = new Map<string, CachedImage>();
  private readonly dropShadowCache = new Map<string, CachedDropShadow>();
  private dropShadowCacheGeneration = 0;
  private readonly blurPass: GlBlurPass;

  constructor(private readonly host: DisplayItemHost) {
    this.blurPass = new GlBlurPass(host.gl);
  }

  drawDisplayItems(items: DisplayItem[]): void {
    this.host.gl.useProgram(this.host.programInfo.program);
    for (const item of items) {
      if (!this.applyItemClip(item.clip)) {
        continue;
      }
      this.drawDisplayItem(item);
    }
    this.host.gl.disable(this.host.gl.SCISSOR_TEST);
  }

  private drawDisplayItem(item: DisplayItem): void {
    if (this.host.renderTrianglesDebug) {
      this.drawTriangleDebugItem(item);
      return;
    }

    const canvasItem = displayItemForCanvas(item);
    const effects = item.effects?.filter(effect => effect.visible) ?? [];
    for (const effect of item.effects ?? []) {
      if (effect.type === "dropShadow" && effect.visible) {
        this.drawDropShadow(canvasItem, effect);
      }
    }

    for (const effect of effects) {
      if (effect.type === "backgroundBlur") {
        this.drawBackgroundBlur(canvasItem, effect);
      }
    }

    const layerBlur = [...effects].reverse().find((effect): effect is LayerBlurEffect => effect.type === "layerBlur" && layerBlurRadius(effect) > 0);
    if (layerBlur) {
      this.drawLayerBlur(canvasItem, layerBlur);
    } else {
      this.drawBaseDisplayItem(item, canvasItem);
    }

    for (const effect of effects) {
      if (effect.type === "innerShadow") {
        this.drawInnerShadow(canvasItem, effect);
      }
    }
  }

  private drawTriangleDebugItem(item: DisplayItem): void {
    const clip = item.clip && shouldRasterizeClip(item.clip)
      ? {
          ...item.clip,
          polygon: fitClipPolygonToUniformLimit(item.clip.polygon, maxShaderClipPointCount),
        }
      : item.clip;

    const vertices = displayItemTriangleVertices(item);
    const primitiveSize = item.kind === "lines" ? 4 : 6;
    if (vertices.length < primitiveSize) {
      return;
    }

    const drawMode = item.kind === "lines" ? this.host.gl.LINES : this.host.gl.TRIANGLES;
    for (let index = 0; index + primitiveSize <= vertices.length; index += primitiveSize) {
      const primitive = vertices.slice(index, index + primitiveSize);
      const color = debugTriangleColorForIndex(item.id, index / primitiveSize);
      this.drawColoredDebugPrimitive(primitive, color, clip, drawMode);
    }
  }

  private drawColoredDebugPrimitive(
    vertices: Float32Array | number[],
    color: [number, number, number, number],
    clip: DisplayClip | undefined,
    drawMode: number,
  ): void {
    if (vertices.length === 0) {
      return;
    }

    const buffer = createTriangleBuffer(this.host.gl, vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, buffer);
    twgl.setUniforms(this.host.programInfo, this.host.paintUniforms({ type: "solid", color }, undefined, clip));
    twgl.drawBufferInfo(this.host.gl, buffer, drawMode);
  }

  private drawBaseDisplayItem(item: DisplayItem, canvasItem: DisplayItem = displayItemForCanvas(item)): void {
    if (shouldRasterizeClip(item.clip)) {
      this.drawRasterizedBaseItem(item);
      return;
    }

    if (item.kind === "text") {
      this.drawTextItem(item);
      return;
    }

    if (item.fill.type === "image") {
      this.drawRasterizedBaseItem(canvasItem);
      return;
    }

    if ((item.kind === "ellipse" || item.kind === "roundedRect") && item.worldTransform) {
      this.drawRasterizedBaseItem(item);
      return;
    }

    if (item.kind === "ellipse") {
      const bufferInfo = this.host.displayTriangleBuffer(item.vertices);
      this.host.gl.useProgram(this.host.ellipseProgramInfo.program);
      twgl.setBuffersAndAttributes(this.host.gl, this.host.ellipseProgramInfo, bufferInfo);
      twgl.setUniforms(this.host.ellipseProgramInfo, {
        ...this.host.paintUniforms(item.fill, item.positionOffset, item.clip),
        u_bounds: [item.bounds.x, item.bounds.y, item.bounds.width, item.bounds.height],
      });
      twgl.drawBufferInfo(this.host.gl, bufferInfo);
      this.host.gl.useProgram(this.host.programInfo.program);
      return;
    }

    if (item.kind === "roundedRect") {
      const bufferInfo = this.host.displayTriangleBuffer(item.vertices);
      this.host.gl.useProgram(this.host.roundedRectProgramInfo.program);
      twgl.setBuffersAndAttributes(this.host.gl, this.host.roundedRectProgramInfo, bufferInfo);
      twgl.setUniforms(this.host.roundedRectProgramInfo, {
        ...this.host.paintUniforms(item.fill, item.positionOffset, item.clip),
        u_bounds: [item.bounds.x, item.bounds.y, item.bounds.width, item.bounds.height],
        u_cornerRadii: item.cornerRadii ?? [0, 0, 0, 0],
      });
      twgl.drawBufferInfo(this.host.gl, bufferInfo);
      this.host.gl.useProgram(this.host.programInfo.program);
      return;
    }

    const bufferInfo = this.host.displayTriangleBuffer(item.vertices);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.programInfo, bufferInfo);
    twgl.setUniforms(this.host.programInfo, this.host.paintUniforms(item.fill, item.positionOffset, item.clip));
    twgl.drawBufferInfo(this.host.gl, bufferInfo, item.kind === "lines" ? this.host.gl.LINES : this.host.gl.TRIANGLES);
  }

  private drawRasterizedBaseItem(item: DisplayItem): void {
    const renderBounds = item.visualBounds ?? item.bounds;
    const clippedBounds = item.clip ? intersectBounds(renderBounds, item.clip.bounds) : renderBounds;
    const visibleBounds = intersectBounds(clippedBounds, this.host.activeViewportDocumentBounds());
    if (visibleBounds.width <= 0 || visibleBounds.height <= 0) {
      return;
    }

    const rasterClip = shouldRasterizeClip(item.clip) ? item.clip : undefined;
    const { canvas } = this.rasterizeItemCanvas(item, visibleBounds, 0, rasterClip);
    this.drawCanvasTexture(canvas, visibleBounds, rasterClip ? undefined : item.clip);
  }

  private drawDropShadow(item: DisplayItem, effect: DropShadowEffect): void {
    const renderBounds = item.visualBounds ?? item.bounds;
    const shadowBounds = dropShadowBounds(renderBounds, effect);
    const rasterBounds = item.clip ? intersectBounds(shadowBounds, item.clip.bounds) : shadowBounds;
    const visibleBounds = intersectBounds(rasterBounds, this.host.activeViewportDocumentBounds());
    if (visibleBounds.width <= 0 || visibleBounds.height <= 0) {
      return;
    }

    // Zoom-independent scale: same document blur (and cache entry) at every zoom.
    const blurSigma = documentBlurToSigma(effect.blur);
    const scale = effectRasterScale(
      blurSigma,
      rasterBounds,
      this.host.maxTextureSize,
      MAX_EFFECT_BLUR_DEVICE_PX,
    );
    const cached = this.getOrCreateDropShadowTexture(item, effect, rasterBounds, scale);
    if (!cached) {
      return;
    }

    this.drawTexturedBounds(cached.texture, rasterBounds, visibleBounds, item.clip);
  }

  private getOrCreateDropShadowTexture(
    item: DisplayItem,
    effect: DropShadowEffect,
    rasterBounds: RenderPreview["bounds"],
    scale: number,
  ): CachedDropShadow | undefined {
    const cacheKey = dropShadowCacheKey(item, effect, scale, this.dropShadowCacheGeneration);
    const existing = this.dropShadowCache.get(cacheKey);
    if (existing) {
      return existing;
    }

    const width = Math.max(1, Math.ceil(rasterBounds.width * scale));
    const height = Math.max(1, Math.ceil(rasterBounds.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }

    const localItem = translateDisplayItem(item, -rasterBounds.x, -rasterBounds.y);
    const [r, g, b, alpha] = oklchToRgba(effect.color);
    context.setTransform(scale, 0, 0, scale, 0, 0);
    // Sharp silhouette — blur runs on the GPU (Safari/WKWebView ignore Canvas2D filter).
    context.fillStyle = `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`;
    this.drawShadowMask(context, localItem, effect);

    const sharp = this.uploadCanvasTexture(canvas);
    if (!sharp) {
      return undefined;
    }

    const blurPx = effect.blur > 0 ? documentBlurToSigma(effect.blur) * scale : 0;
    const texture = blurPx > 0 ? this.blurPass.blur(sharp.texture, sharp.width, sharp.height, blurPx) : sharp.texture;
    if (blurPx > 0) {
      this.host.gl.deleteTexture(sharp.texture);
    }

    const entry: CachedDropShadow = { texture, width, height };
    if (this.dropShadowCache.size >= DROP_SHADOW_CACHE_LIMIT) {
      const oldestKey = this.dropShadowCache.keys().next().value;
      if (oldestKey !== undefined) {
        const evicted = this.dropShadowCache.get(oldestKey);
        if (evicted) {
          this.host.gl.deleteTexture(evicted.texture);
        }
        this.dropShadowCache.delete(oldestKey);
      }
    }
    this.dropShadowCache.set(cacheKey, entry);
    return entry;
  }

  private drawTexturedBounds(
    texture: WebGLTexture,
    rasterBounds: RenderPreview["bounds"],
    drawBounds: RenderPreview["bounds"],
    clip?: DisplayClip,
  ): void {
    const u0 = (drawBounds.x - rasterBounds.x) / rasterBounds.width;
    const v0 = (drawBounds.y - rasterBounds.y) / rasterBounds.height;
    const u1 = u0 + drawBounds.width / rasterBounds.width;
    const v1 = v0 + drawBounds.height / rasterBounds.height;
    const x = drawBounds.x;
    const y = drawBounds.y;
    const right = drawBounds.x + drawBounds.width;
    const bottom = drawBounds.y + drawBounds.height;

    this.host.gl.bindTexture(this.host.gl.TEXTURE_2D, texture);
    const buffer = twgl.createBufferInfoFromArrays(this.host.gl, {
      position: {
        numComponents: 2,
        data: [x, y, right, y, x, bottom, x, bottom, right, y, right, bottom],
      },
      texcoord: {
        numComponents: 2,
        data: [u0, v0, u1, v0, u0, v1, u0, v1, u1, v0, u1, v1],
      },
    });
    this.host.gl.useProgram(this.host.textureProgramInfo.program);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.textureProgramInfo, buffer);
    twgl.setUniforms(this.host.textureProgramInfo, {
      ...this.host.baseUniforms(),
      u_texture: texture,
      ...neutralTextureUniforms(),
      ...clipUniforms(clip),
    });
    twgl.drawBufferInfo(this.host.gl, buffer);
    this.host.gl.useProgram(this.host.programInfo.program);
  }

  clearDropShadowCache(): void {
    for (const entry of this.dropShadowCache.values()) {
      this.host.gl.deleteTexture(entry.texture);
    }
    this.dropShadowCache.clear();
    this.dropShadowCacheGeneration += 1;
  }

  clearTextTextureCache(): void {
    for (const cached of this.textTextureCache.values()) {
      this.host.gl.deleteTexture(cached.texture);
    }
    this.textTextureCache.clear();
  }

  clearImageCache(): void {
    for (const cached of this.imageCache.values()) {
      if (cached.texture) {
        this.host.gl.deleteTexture(cached.texture);
      }
    }
    this.imageCache.clear();
  }

  private drawLayerBlur(item: DisplayItem, effect: LayerBlurEffect): void {
    const radius = layerBlurRadius(effect);
    const blurSigma = documentBlurToSigma(radius);
    // Expand by kernel support (~2.5σ) so soft edges aren't hard-clipped.
    const pad = blurSigma * 3;
    const blurBounds = expandedBounds(item.visualBounds ?? item.bounds, pad);
    const clippedBounds = item.clip ? intersectBounds(blurBounds, item.clip.bounds) : blurBounds;
    const visibleBounds = intersectBounds(clippedBounds, this.host.activeViewportDocumentBounds());
    if (visibleBounds.width <= 0 || visibleBounds.height <= 0) {
      return;
    }

    // Rasterize with an apron into neighboring tiles so the Gaussian can sample
    // across tile edges; draw only the visible (tile) subrect after blur.
    const sampleBounds = intersectBounds(expandedBounds(visibleBounds, pad), clippedBounds);
    const rasterClip = shouldRasterizeClip(item.clip) ? item.clip : undefined;
    const { canvas, blurDevicePx } = this.rasterizeItemCanvas(item, sampleBounds, blurSigma, rasterClip);
    this.drawCanvasTexture(canvas, sampleBounds, rasterClip ? undefined : item.clip, blurDevicePx, visibleBounds);
  }

  private drawBackgroundBlur(item: DisplayItem, effect: BackgroundBlurEffect): void {
    if (effect.blur <= 0 || !this.host.shouldRenderBackgroundBlur()) {
      return;
    }

    const renderBounds = item.visualBounds ?? item.bounds;
    const clippedBounds = item.clip ? intersectBounds(renderBounds, item.clip.bounds) : renderBounds;
    const visibleBounds = intersectBounds(clippedBounds, this.host.activeViewportDocumentBounds());
    if (visibleBounds.width <= 0 || visibleBounds.height <= 0) {
      return;
    }

    const samplePadding = documentBlurToSigma(effect.blur) * 3;
    const sampleBounds = intersectBounds(
      item.clip ? intersectBounds(expandedBounds(clippedBounds, samplePadding), item.clip.bounds) : expandedBounds(clippedBounds, samplePadding),
      this.host.activeViewportDocumentBounds(),
    );
    if (sampleBounds.width <= 0 || sampleBounds.height <= 0) {
      return;
    }

    const pixelRect = documentBoundsToPixelRect(sampleBounds, this.host.activeCamera(), this.host.activeResolution());
    if (pixelRect.width <= 0 || pixelRect.height <= 0) {
      return;
    }

    this.host.gl.finish();
    const pixels = new Uint8Array(pixelRect.width * pixelRect.height * 4);
    this.host.gl.readPixels(pixelRect.x, pixelRect.y, pixelRect.width, pixelRect.height, this.host.gl.RGBA, this.host.gl.UNSIGNED_BYTE, pixels);
    const sourceCanvas = document.createElement("canvas");
    sourceCanvas.width = pixelRect.width;
    sourceCanvas.height = pixelRect.height;
    const sourceContext = sourceCanvas.getContext("2d");
    if (!sourceContext) {
      return;
    }
    const imageData = sourceContext.createImageData(pixelRect.width, pixelRect.height);
    imageData.data.set(flipPixelRows(pixels, pixelRect.width, pixelRect.height));
    sourceContext.putImageData(imageData, 0, 0);

    const canvas = document.createElement("canvas");
    canvas.width = pixelRect.width;
    canvas.height = pixelRect.height;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    const scale = pixelRect.width / Math.max(0.0001, sampleBounds.width);
    // Blur the backdrop on the GPU, then mask to the silhouette in Canvas2D
    // (destination-in does not need ctx.filter — works in Safari/WKWebView).
    context.drawImage(sourceCanvas, 0, 0);
    const sharp = this.uploadCanvasTexture(canvas);
    if (!sharp) {
      return;
    }
    const blurred = this.blurPass.blur(sharp.texture, sharp.width, sharp.height, documentBlurToSigma(effect.blur) * scale);
    this.host.gl.deleteTexture(sharp.texture);
    this.readTextureIntoCanvas(blurred, canvas);
    this.host.gl.deleteTexture(blurred);

    context.globalCompositeOperation = "destination-in";
    context.setTransform(scale, 0, 0, scale, -sampleBounds.x * scale, -sampleBounds.y * scale);
    context.fillStyle = "#ffffff";
    this.drawItemSilhouette(context, item);
    context.globalCompositeOperation = "source-over";
    context.setTransform(1, 0, 0, 1, 0, 0);

    this.drawCanvasTexture(canvas, sampleBounds, item.clip);
  }

  private drawInnerShadow(item: DisplayItem, effect: InnerShadowEffect): void {
    const renderBounds = item.visualBounds ?? item.bounds;
    const blurSigma = documentBlurToSigma(effect.blur);
    const pad = blurSigma * 3;
    const effectBounds = expandedBounds(renderBounds, pad);
    const clippedBounds = item.clip ? intersectBounds(effectBounds, item.clip.bounds) : effectBounds;
    const visibleBounds = intersectBounds(clippedBounds, this.host.activeViewportDocumentBounds());
    if (visibleBounds.width <= 0 || visibleBounds.height <= 0) {
      return;
    }

    // Apron so the punch-out blur isn't clipped at tile edges.
    const sampleBounds = intersectBounds(expandedBounds(visibleBounds, pad), clippedBounds);

    // Zoom-independent scale so punch-out softness matches drop shadow / layer blur.
    const scale = effectRasterScale(
      blurSigma,
      sampleBounds,
      this.host.maxTextureSize,
      MAX_EFFECT_BLUR_DEVICE_PX,
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(sampleBounds.width * scale));
    canvas.height = Math.max(1, Math.ceil(sampleBounds.height * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const [r, g, b, alpha] = oklchToRgba(effect.color);
    context.setTransform(scale, 0, 0, scale, -sampleBounds.x * scale, -sampleBounds.y * scale);
    context.fillStyle = `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`;
    this.drawItemSilhouette(context, item);

    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = canvas.width;
    maskCanvas.height = canvas.height;
    const maskContext = maskCanvas.getContext("2d");
    if (!maskContext) {
      this.drawCanvasTexture(canvas, sampleBounds, item.clip, 0, visibleBounds);
      return;
    }
    maskContext.setTransform(scale, 0, 0, scale, -sampleBounds.x * scale, -sampleBounds.y * scale);
    maskContext.fillStyle = "#ffffff";
    this.drawShadowMask(maskContext, item, effect);

    const blurPx = effect.blur > 0 ? blurSigma * scale : 0;
    if (blurPx > 0) {
      const sharpMask = this.uploadCanvasTexture(maskCanvas);
      if (sharpMask) {
        const blurredMask = this.blurPass.blur(sharpMask.texture, sharpMask.width, sharpMask.height, blurPx);
        this.host.gl.deleteTexture(sharpMask.texture);
        this.readTextureIntoCanvas(blurredMask, maskCanvas);
        this.host.gl.deleteTexture(blurredMask);
      }
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalCompositeOperation = "destination-out";
    context.drawImage(maskCanvas, 0, 0);
    context.globalCompositeOperation = "source-over";

    this.drawCanvasTexture(canvas, sampleBounds, item.clip, 0, visibleBounds);
  }

  private rasterizeItemCanvas(
    item: DisplayItem,
    visibleBounds: RenderPreview["bounds"],
    /** Device-space Gaussian σ budget input (already document→σ mapped). */
    blurSigma: number,
    clip?: DisplayClip,
  ): { canvas: HTMLCanvasElement; blurDevicePx: number } {
    const camera = this.host.activeCamera();
    const byTexture = Math.min(
      this.host.maxTextureSize / Math.max(1, visibleBounds.width),
      this.host.maxTextureSize / Math.max(1, visibleBounds.height),
    );
    // Soft effects stay zoom-invariant. Sharp fills (rotated primitives, images, clips)
    // must match the active camera / tile zoomBucket or they upsample soft bitmaps.
    const scale = blurSigma <= 1e-6
      ? Math.max(0.25, Math.min(camera.zoom * camera.devicePixelRatio, byTexture))
      : effectRasterScale(
        blurSigma,
        visibleBounds,
        this.host.maxTextureSize,
        MAX_EFFECT_BLUR_DEVICE_PX,
      );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(visibleBounds.width * scale));
    canvas.height = Math.max(1, Math.ceil(visibleBounds.height * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      return { canvas, blurDevicePx: 0 };
    }

    context.setTransform(scale, 0, 0, scale, -visibleBounds.x * scale, -visibleBounds.y * scale);
    if (clip && clip.polygon.length >= 3) {
      applyCanvasClip(context, clip);
    }
    // Sharp raster — layer blur is applied in drawLayerBlur via WebGL.
    this.drawItemFill(context, item);
    return { canvas, blurDevicePx: blurSigma > 0 ? blurSigma * scale : 0 };
  }

  private drawCanvasTexture(
    canvas: HTMLCanvasElement,
    rasterBounds: RenderPreview["bounds"],
    clip?: DisplayClip,
    blurPx = 0,
    drawBounds: RenderPreview["bounds"] = rasterBounds,
  ): void {
    const uploaded = this.uploadCanvasTexture(canvas);
    if (!uploaded) {
      return;
    }

    let texture = uploaded.texture;
    if (blurPx > 0) {
      texture = this.blurPass.blur(uploaded.texture, uploaded.width, uploaded.height, blurPx);
      this.host.gl.deleteTexture(uploaded.texture);
    }

    this.drawTexturedBounds(texture, rasterBounds, drawBounds, clip);
    this.host.gl.deleteTexture(texture);
    this.host.gl.useProgram(this.host.programInfo.program);
  }

  private uploadCanvasTexture(canvas: HTMLCanvasElement): { texture: WebGLTexture; width: number; height: number } | undefined {
    const texture = this.host.gl.createTexture();
    if (!texture) {
      return undefined;
    }

    this.host.gl.bindTexture(this.host.gl.TEXTURE_2D, texture);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_MIN_FILTER, this.host.gl.LINEAR);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_MAG_FILTER, this.host.gl.LINEAR);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_WRAP_S, this.host.gl.CLAMP_TO_EDGE);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_WRAP_T, this.host.gl.CLAMP_TO_EDGE);
    this.host.gl.pixelStorei(this.host.gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    this.host.gl.texImage2D(this.host.gl.TEXTURE_2D, 0, this.host.gl.RGBA, this.host.gl.RGBA, this.host.gl.UNSIGNED_BYTE, canvas);
    return { texture, width: canvas.width, height: canvas.height };
  }

  /** Read a WebGL texture back into a canvas (for Canvas2D compositing after a GPU blur). */
  private readTextureIntoCanvas(texture: WebGLTexture, canvas: HTMLCanvasElement): void {
    const gl = this.host.gl;
    const width = canvas.width;
    const height = canvas.height;
    const framebuffer = gl.createFramebuffer();
    if (!framebuffer) {
      return;
    }

    const prevFb = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, prevFb);
    gl.deleteFramebuffer(framebuffer);

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    // Textures from canvas upload / GlBlurPass store image-top at UV t=0, which is
    // also readPixels row 0 — do not flip (unlike the main framebuffer capture).
    const imageData = context.createImageData(width, height);
    imageData.data.set(pixels);
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, width, height);
    context.putImageData(imageData, 0, 0);
  }

  private drawItemFill(context: CanvasRenderingContext2D, item: DisplayItem): void {
    if (item.kind === "text") {
      this.drawTextFill(context, item);
      return;
    }

    if (item.fill.type === "image") {
      this.drawImageFill(context, item);
      return;
    }

    context.save();
    if (item.worldTransform) {
      applyCanvasAffineTransform(context, item.worldTransform);
    }
    context.fillStyle = canvasFillForDisplayPaint(context, item.fill);
    this.drawItemSilhouette(context, item);
    context.restore();
  }

  private drawImageFill(context: CanvasRenderingContext2D, item: DisplayItem): void {
    if (item.fill.type !== "image") {
      return;
    }

    const image = this.imageForPaint(item.fill);
    if (!image) {
      return;
    }

    context.save();
    context.globalAlpha = item.fill.opacity;
    context.filter = canvasFilterForImagePaint(item.fill);
    const scaleX = item.scaleX ?? 1;
    const scaleY = item.scaleY ?? 1;
    const hasScale = scaleX !== 1 || scaleY !== 1;
    const hasRotation = Boolean(item.rotation && item.rotation !== 0);
    if (item.worldTransform) {
      applyCanvasAffineTransform(context, item.worldTransform);
      if (hasScale) {
        const center = item.rotationOrigin ?? rectCenter(item.bounds);
        context.translate(center.x, center.y);
        context.scale(scaleX, scaleY);
        context.translate(-center.x, -center.y);
      }
    } else if (hasRotation || hasScale) {
      const center = item.rotationOrigin ?? rectCenter(item.bounds);
      context.translate(center.x, center.y);
      if (hasRotation) {
        context.rotate((item.rotation! * Math.PI) / 180);
      }
      if (hasScale) {
        context.scale(scaleX, scaleY);
      }
      context.translate(-center.x, -center.y);
    }
    this.drawImagePaint(context, image, item.fill);
    context.filter = "none";
    applyImageAdjustmentOverlays(context, item.fill);
    context.globalAlpha = 1;
    context.globalCompositeOperation = "destination-in";
    context.fillStyle = "#ffffff";
    this.drawItemSilhouette(context, item);
    context.restore();
  }

  private drawImagePaint(context: CanvasRenderingContext2D, image: CanvasImageSource, paint: Extract<DisplayPaint, { type: "image" }>): void {
    const rect = displayImageDrawRect(paint);
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  }

  private imageForPaint(paint: Extract<DisplayPaint, { type: "image" }>): HTMLImageElement | undefined {
    const source = rasterAssetDataUrl(paint.assetId, paint.source);
    if (!source) {
      return undefined;
    }
    const cacheKey = paint.assetId ?? source;
    const cached = this.imageCache.get(cacheKey);
    if (cached) {
      return cached.loaded && !cached.failed ? cached.image : undefined;
    }

    const image = new Image();
    const entry = { image, loaded: false, failed: false };
    this.imageCache.set(cacheKey, entry);
    image.onload = () => {
      entry.loaded = true;
      this.host.reloadAfterImageLoad();
    };
    image.onerror = () => {
      entry.failed = true;
    };
    image.src = source;
    if (image.complete && image.naturalWidth > 0) {
      entry.loaded = true;
      return image;
    }
    return undefined;
  }

  async preloadDisplayListImages(items: DisplayItem[]): Promise<void> {
    const paints = items
      .map(item => item.fill)
      .filter((paint): paint is Extract<DisplayPaint, { type: "image" }> => paint.type === "image");
    await Promise.all(paints.map(paint => this.preloadImagePaint(paint)));
  }

  private async preloadImagePaint(paint: Extract<DisplayPaint, { type: "image" }>): Promise<void> {
    const source = rasterAssetDataUrl(paint.assetId, paint.source);
    if (!source) {
      return;
    }

    const cacheKey = paint.assetId ?? source;
    const cached = this.imageCache.get(cacheKey);
    if (cached?.loaded || cached?.failed) {
      return;
    }
    // Two paints can share one asset in the same display list; without deduping
    // the in-flight load, a second concurrent preload would overwrite the first
    // image's onload/onerror handler and its promise would never resolve.
    if (cached?.loadPromise) {
      await cached.loadPromise;
      return;
    }

    const image = cached?.image ?? new Image();
    const entry: CachedImage = cached ?? { image, loaded: false, failed: false };
    this.imageCache.set(cacheKey, entry);
    const loadPromise = new Promise<void>(resolve => {
      image.onload = () => {
        entry.loaded = true;
        entry.loadPromise = undefined;
        resolve();
      };
      image.onerror = () => {
        entry.failed = true;
        entry.loadPromise = undefined;
        resolve();
      };
      if (!cached) {
        image.src = source;
      }
      if (image.complete && image.naturalWidth > 0) {
        entry.loaded = true;
        entry.loadPromise = undefined;
        resolve();
      }
    });
    entry.loadPromise = loadPromise;
    await loadPromise;
  }

  private drawItemSilhouette(context: CanvasRenderingContext2D, item: DisplayItem): void {
    if (item.kind === "text") {
      this.drawTextShadowMask(context, item, { offsetX: 0, offsetY: 0 } as DropShadowEffect);
      return;
    }

    if (item.kind === "ellipse") {
      context.beginPath();
      context.ellipse(item.bounds.x + item.bounds.width / 2, item.bounds.y + item.bounds.height / 2, Math.abs(item.bounds.width / 2), Math.abs(item.bounds.height / 2), 0, 0, Math.PI * 2);
      context.fill();
      return;
    }

    if (item.kind === "roundedRect") {
      roundedRectPath(context, item.bounds, item.cornerRadii ?? [0, 0, 0, 0]);
      context.fill();
      return;
    }

    if (item.kind === "lines") {
      context.lineWidth = 1;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.beginPath();
      for (let index = 0; index < item.vertices.length; index += 4) {
        context.moveTo(item.vertices[index]!, item.vertices[index + 1]!);
        context.lineTo(item.vertices[index + 2]!, item.vertices[index + 3]!);
      }
      context.strokeStyle = context.fillStyle;
      context.stroke();
      return;
    }

    context.beginPath();
    for (let index = 0; index < item.vertices.length; index += 6) {
      context.moveTo(item.vertices[index]!, item.vertices[index + 1]!);
      context.lineTo(item.vertices[index + 2]!, item.vertices[index + 3]!);
      context.lineTo(item.vertices[index + 4]!, item.vertices[index + 5]!);
      context.closePath();
    }
    context.fill();
  }

  private drawTextFill(context: CanvasRenderingContext2D, item: DisplayItem): void {
    if (!item.text) {
      return;
    }
    const textNode = displayItemToTextNode(item);
    const layout = getCachedTextLayout(textNode, Math.round(this.host.activeCamera().zoom * this.host.activeCamera().devicePixelRatio * 10));
    const offsetY = verticalTextOffset(textNode.bounds.height, layout.height, textNode.text.style.verticalAlign);
    context.save();
    if (item.worldTransform) {
      applyCanvasAffineTransform(context, item.worldTransform);
    }
    const scaleX = item.scaleX ?? 1;
    const scaleY = item.scaleY ?? 1;
    if (scaleX !== 1 || scaleY !== 1) {
      const centerX = item.bounds.x + item.bounds.width / 2;
      const centerY = item.bounds.y + item.bounds.height / 2;
      context.translate(centerX, centerY);
      context.scale(scaleX, scaleY);
      context.translate(-centerX, -centerY);
    }
    context.translate(item.bounds.x, item.bounds.y);
    for (const run of layout.runs) {
      const fill = run.fill ?? item.textFill;
      if (!fill) {
        continue;
      }

      context.font = `${run.style.fontWeight} ${run.style.fontSize}px "${run.style.fontFamily}", sans-serif`;
      context.fillStyle = paintToCanvasFillStyle(context, fill, { x: 0, y: 0, width: textNode.bounds.width, height: textNode.bounds.height }) ?? "transparent";
      drawTextWithLetterSpacing(context, run.text, run.x, offsetY + run.y + run.baseline, run.style.letterSpacing);
    }
    context.restore();
  }

  private drawShadowMask(context: CanvasRenderingContext2D, item: DisplayItem, effect: DropShadowEffect | InnerShadowEffect): void {
    if (item.kind === "text") {
      this.drawTextShadowMask(context, item, effect);
      return;
    }

    if (item.kind === "ellipse") {
      const bounds = offsetSpreadBounds(item.bounds, effect);
      context.beginPath();
      context.ellipse(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2, Math.abs(bounds.width / 2), Math.abs(bounds.height / 2), 0, 0, Math.PI * 2);
      context.fill();
      return;
    }

    if (item.kind === "roundedRect") {
      const bounds = offsetSpreadBounds(item.bounds, effect);
      roundedRectPath(context, bounds, (item.cornerRadii ?? [0, 0, 0, 0]).map(radius => radius + effect.spread) as [number, number, number, number]);
      context.fill();
      return;
    }

    if (item.kind === "lines") {
      context.lineWidth = Math.max(1, effect.spread * 2 + 1);
      context.lineCap = "round";
      context.lineJoin = "round";
      context.beginPath();
      for (let index = 0; index < item.vertices.length; index += 4) {
        const from = shadowPoint(item.vertices[index]!, item.vertices[index + 1]!, item.bounds, effect);
        const to = shadowPoint(item.vertices[index + 2]!, item.vertices[index + 3]!, item.bounds, effect);
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
      }
      context.strokeStyle = context.fillStyle;
      context.stroke();
      return;
    }

    drawShadowShapeMask(context, item, effect);
  }

  private drawTextShadowMask(context: CanvasRenderingContext2D, item: DisplayItem, effect: Pick<DropShadowEffect, "offsetX" | "offsetY">): void {
    if (!item.text) {
      return;
    }

    const textNode = displayItemToTextNode(item);
    const layout = getCachedTextLayout(textNode, Math.round(this.host.activeCamera().zoom * this.host.activeCamera().devicePixelRatio * 10));
    const offsetY = verticalTextOffset(textNode.bounds.height, layout.height, textNode.text.style.verticalAlign);
    context.save();
    const scaleX = item.scaleX ?? 1;
    const scaleY = item.scaleY ?? 1;
    if (scaleX !== 1 || scaleY !== 1) {
      const centerX = item.bounds.x + item.bounds.width / 2;
      const centerY = item.bounds.y + item.bounds.height / 2;
      context.translate(centerX, centerY);
      context.scale(scaleX, scaleY);
      context.translate(-centerX, -centerY);
    }
    context.translate(item.bounds.x + effect.offsetX, item.bounds.y + effect.offsetY);
    for (const run of layout.runs) {
      context.font = `${run.style.fontWeight} ${run.style.fontSize}px "${run.style.fontFamily}", sans-serif`;
      drawTextWithLetterSpacing(context, run.text, run.x, offsetY + run.y + run.baseline, run.style.letterSpacing);
    }
    context.restore();
  }

  private applyItemClip(clip: DisplayClip | undefined): boolean {
    const gl = this.host.gl;
    if (!clip) {
      gl.disable(gl.SCISSOR_TEST);
      return true;
    }

    const camera = this.host.activeCamera();
    const [resolutionWidth, resolutionHeight] = this.host.activeResolution();
    const scale = camera.zoom * camera.devicePixelRatio;
    const bounds = clip.bounds;
    const left = (bounds.x - camera.x) * scale;
    const top = (bounds.y - camera.y) * scale;
    const right = (bounds.x + bounds.width - camera.x) * scale;
    const bottom = (bounds.y + bounds.height - camera.y) * scale;
    const x1 = Math.max(0, Math.min(resolutionWidth, Math.floor(Math.min(left, right))));
    const x2 = Math.max(0, Math.min(resolutionWidth, Math.ceil(Math.max(left, right))));
    const y1 = Math.max(0, Math.min(resolutionHeight, Math.floor(resolutionHeight - Math.max(top, bottom))));
    const y2 = Math.max(0, Math.min(resolutionHeight, Math.ceil(resolutionHeight - Math.min(top, bottom))));
    const width = x2 - x1;
    const height = y2 - y1;

    if (width <= 0 || height <= 0) {
      return false;
    }

    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x1, y1, width, height);
    return true;
  }

  private drawTextItem(item: DisplayItem): void {
    if (!item.text) {
      return;
    }

    const textNode = displayItemToTextNode(item);
    const camera = this.host.activeCamera();
    const layout = getCachedTextLayout(textNode, Math.round(camera.zoom * camera.devicePixelRatio * 10));
    const width = item.text.textLayout === "AUTO_WIDTH" ? layout.width : Math.max(item.bounds.width, layout.width);
    const height = Math.max(item.bounds.height, layout.height);
    const textBounds = { x: item.bounds.x, y: item.bounds.y, width, height };
    const visualBounds = item.worldTransform ? boundsFromAffineRect(item.worldTransform, textBounds) : textBounds;
    const visibleVisualBounds = item.clip
      ? intersectBounds(intersectBounds(visualBounds, this.host.activeViewportDocumentBounds()), item.clip.bounds)
      : intersectBounds(visualBounds, this.host.activeViewportDocumentBounds());
    if (visibleVisualBounds.width <= 0 || visibleVisualBounds.height <= 0) {
      return;
    }

    const texture = this.getTextTexture(textNode, item, layout, textBounds);
    const vertices = item.worldTransform
      ? transformRectVertices(textBounds, item.worldTransform)
      : rectVertices(textBounds);
    const buffer = createTexturedQuadBuffer(this.host.gl, vertices);
    this.host.gl.useProgram(this.host.textureProgramInfo.program);
    twgl.setBuffersAndAttributes(this.host.gl, this.host.textureProgramInfo, buffer);
    twgl.setUniforms(this.host.textureProgramInfo, {
      ...this.host.baseUniforms(),
      u_texture: texture.texture,
      ...neutralTextureUniforms(),
      ...clipUniforms(item.clip),
    });
    twgl.drawBufferInfo(this.host.gl, buffer);
    this.host.gl.useProgram(this.host.programInfo.program);
  }

  private getTextTexture(
    node: Extract<SceneNode, { type: "text" }>,
    item: DisplayItem,
    layout: ReturnType<typeof getCachedTextLayout>,
    textBounds: RenderPreview["bounds"],
  ): { texture: WebGLTexture; width: number; height: number } {
    const camera = this.host.activeCamera();
    const pixelScale = Math.max(1, Math.ceil(camera.zoom * camera.devicePixelRatio));
    const width = Math.max(1, Math.min(this.host.maxTextureSize, Math.ceil(textBounds.width * pixelScale)));
    const height = Math.max(1, Math.min(this.host.maxTextureSize, Math.ceil(textBounds.height * pixelScale)));
    const cacheKey = `${item.id}:${layout.key}:${pixelScale}:${formatCacheNumber(textBounds.x)}:${formatCacheNumber(textBounds.y)}:${width}:${height}`;
    const cached = this.textTextureCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (context) {
      context.scale(pixelScale, pixelScale);
      context.textBaseline = "alphabetic";
      const offsetY = verticalTextOffset(node.bounds.height, layout.height, node.text.style.verticalAlign);
      for (const run of layout.runs) {
        const fill = run.fill ?? item.textFill;
        if (!fill) {
          continue;
        }

        context.font = `${run.style.fontWeight} ${run.style.fontSize}px "${run.style.fontFamily}", sans-serif`;
        context.fillStyle = paintToCanvasFillStyle(context, fill, { x: 0, y: 0, width: node.bounds.width, height: node.bounds.height }) ?? "transparent";
        drawTextWithLetterSpacing(context, run.text, run.x, offsetY + run.y + run.baseline, run.style.letterSpacing);
      }
    }

    const texture = this.host.gl.createTexture();
    if (!texture) {
      throw new Error("Failed to create text texture.");
    }
    this.host.gl.bindTexture(this.host.gl.TEXTURE_2D, texture);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_MIN_FILTER, this.host.gl.LINEAR);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_MAG_FILTER, this.host.gl.LINEAR);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_WRAP_S, this.host.gl.CLAMP_TO_EDGE);
    this.host.gl.texParameteri(this.host.gl.TEXTURE_2D, this.host.gl.TEXTURE_WRAP_T, this.host.gl.CLAMP_TO_EDGE);
    this.host.gl.pixelStorei(this.host.gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    this.host.gl.texImage2D(this.host.gl.TEXTURE_2D, 0, this.host.gl.RGBA, this.host.gl.RGBA, this.host.gl.UNSIGNED_BYTE, canvas);

    const next = { texture, width, height };
    this.textTextureCache.set(cacheKey, next);
    if (this.textTextureCache.size > 200) {
      const firstKey = this.textTextureCache.keys().next().value;
      const first = firstKey ? this.textTextureCache.get(firstKey) : undefined;
      if (firstKey && first) {
        this.host.gl.deleteTexture(first.texture);
        this.textTextureCache.delete(firstKey);
      }
    }
    return next;
  }
}
